import { useMemo } from 'react';
import { Progress } from '@/components/ui/progress';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordStrengthProps {
  password: string;
  className?: string;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

export const PasswordStrength = ({ password, className }: PasswordStrengthProps) => {
  const requirements: PasswordRequirement[] = [
    {
      label: 'At least 8 characters',
      test: (pwd) => pwd.length >= 8
    },
    {
      label: 'Contains uppercase letter',
      test: (pwd) => /[A-Z]/.test(pwd)
    },
    {
      label: 'Contains lowercase letter',
      test: (pwd) => /[a-z]/.test(pwd)
    },
    {
      label: 'Contains number',
      test: (pwd) => /\d/.test(pwd)
    },
    {
      label: 'Contains special character',
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
    }
  ];

  const { strength, score, color } = useMemo(() => {
    if (!password) {
      return { strength: '', score: 0, color: 'bg-muted' };
    }

    const passedRequirements = requirements.filter(req => req.test(password)).length;
    
    if (passedRequirements <= 2) {
      return { strength: 'Weak', score: 25, color: 'bg-destructive' };
    } else if (passedRequirements <= 3) {
      return { strength: 'Fair', score: 50, color: 'bg-orange-500' };
    } else if (passedRequirements <= 4) {
      return { strength: 'Good', score: 75, color: 'bg-yellow-500' };
    } else {
      return { strength: 'Strong', score: 100, color: 'bg-green-500' };
    }
  }, [password]);

  if (!password) return null;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Password strength:</span>
          <span className={cn(
            "font-medium",
            score <= 25 && "text-destructive",
            score > 25 && score <= 50 && "text-orange-500",
            score > 50 && score <= 75 && "text-yellow-600",
            score > 75 && "text-green-600"
          )}>
            {strength}
          </span>
        </div>
        <div className="relative">
          <Progress value={score} className="h-2" />
          <div 
            className={cn("absolute top-0 left-0 h-2 rounded-full transition-all", color)}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>
      
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Requirements:</p>
        <ul className="space-y-1">
          {requirements.map((requirement, index) => {
            const passed = requirement.test(password);
            return (
              <li key={index} className="flex items-center space-x-2 text-xs">
                <div className={cn(
                  "w-4 h-4 rounded-full flex items-center justify-center",
                  passed ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
                )}>
                  {passed ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                </div>
                <span className={cn(
                  passed ? "text-green-700" : "text-muted-foreground"
                )}>
                  {requirement.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};